// Ragdolls for the 3D look: a soldier who is killed becomes a jointed body (head, chest, pelvis,
// upper and lower arms and legs) run by Verlet physics, so he crumples, is thrown by what hit him,
// folds over his own joints, slides and settles the way a body does. A blast can tear limbs off:
// the joint lets go and the limb flies on its own, trailing blood. Bodies lie a while and then
// sink away. Purely visual; the deterministic sim never sees it.
import * as THREE from 'three';

const GRAVITY = 9.8;
/** Physics steps a second, and passes over the joints each step: enough for a body to hold together. */
const STEP = 1 / 90;
const ITERATIONS = 10;
/** Of the speed along the ground, how much a body part keeps each second it touches it. */
const GROUND_KEEP = 0.02;
const LIE_MS = 7000;
const SINK_MS = 1500;
const MAX_BODIES = 60;

/** The points of a body: head, chest, pelvis, and an elbow, hand, knee and foot each side. */
const P = { head: 0, chest: 1, pelvis: 2, lElbow: 3, lHand: 4, rElbow: 5, rHand: 6, lKnee: 7, lFoot: 8, rKnee: 9, rFoot: 10 } as const;
const N = 11;

/** Where each point sits on a man standing, facing +X, in tiles: a soldier is about half a tile tall. */
const POSE: [number, number, number][] = [
  [0, 0.5, 0],          // head
  [0, 0.38, 0],         // chest
  [0, 0.25, 0],         // pelvis
  [0, 0.3, 0.09],       // left elbow
  [0.04, 0.22, 0.1],    // left hand
  [0, 0.3, -0.09],      // right elbow
  [0.06, 0.24, -0.08],  // right hand
  [0.02, 0.13, 0.04],   // left knee
  [0, 0.01, 0.045],     // left foot
  [0.02, 0.13, -0.04],  // right knee
  [0, 0.01, -0.045],    // right foot
];

/** The bones, drawn as capsules: [from, to, radius, part]. The first ones of each limb attach it to the body. */
const BONES: [number, number, number, 'uniform' | 'skin' | 'boot' | 'trousers'][] = [
  [P.chest, P.pelvis, 0.05, 'uniform'],
  [P.chest, P.lElbow, 0.022, 'uniform'],
  [P.lElbow, P.lHand, 0.019, 'skin'],
  [P.chest, P.rElbow, 0.022, 'uniform'],
  [P.rElbow, P.rHand, 0.019, 'skin'],
  [P.pelvis, P.lKnee, 0.03, 'trousers'],
  [P.lKnee, P.lFoot, 0.026, 'boot'],
  [P.pelvis, P.rKnee, 0.03, 'trousers'],
  [P.rKnee, P.rFoot, 0.026, 'boot'],
];
/** Capsules drawn to a body. */
const LIMBS = BONES.length;
/** Anatomical length of each bone at rest, so no limb can ever stretch past its true length. */
const REST_LEN = BONES.map(([a, b]) => {
  const pa = POSE[a]!, pb = POSE[b]!;
  return Math.hypot(pa[0] - pb[0], pa[1] - pb[1], pa[2] - pb[2]);
});
/** The joints a limb is torn off at: the bone index joining it to the body. */
const LIMB_ROOTS = [1, 3, 5, 7];

/** Stiffeners, not drawn: they keep the spine and the hips from folding flat in impossible ways. */
const BRACES: [number, number][] = [[P.head, P.pelvis], [P.head, P.chest], [P.lKnee, P.rKnee], [P.chest, P.lKnee], [P.chest, P.rKnee]];

interface Stick { a: number; b: number; len: number }

interface Body {
  pos: Float32Array;      // N points, x y z
  prev: Float32Array;
  sticks: Stick[];
  /** Each bone's colour, then the head's. */
  colors: number[];
  /** Bones torn off: their mesh still drawn, the joint to the body gone. */
  torn: Set<number>;
  at: number;
  bleedUntil: number;
}

/** A capsule four units long end to end (a two-unit body and two rounded ends), one unit thick. */
const capsule = new THREE.CapsuleGeometry(1, 2, 3, 8);
const head = new THREE.SphereGeometry(1, 10, 8);
capsule.userData.shared = head.userData.shared = true;

const tmpA = new THREE.Vector3(), tmpB = new THREE.Vector3(), tmpD = new THREE.Vector3();
const tmpQ = new THREE.Quaternion(), tmpS = new THREE.Vector3(), tmpM = new THREE.Matrix4(), tmpC = new THREE.Color();
const NO_TURN = new THREE.Quaternion();
const UP = new THREE.Vector3(0, 1, 0);

export interface RagdollLook { uniform: number; skin?: number; boot?: number; trousers?: number }

export class Ragdolls {
  readonly group = new THREE.Group();
  private bodies: Body[] = [];
  private acc = 0;
  private last = 0;
  // every body is drawn in two calls, limbs and heads, coloured part by part: as a mesh to each
  // limb, a battlefield of the fallen was hundreds of draws in the view and as many in the shadows
  private readonly limbs: THREE.InstancedMesh;
  private readonly heads: THREE.InstancedMesh;
  /** Called with a point and a direction where a torn limb is bleeding, so the effects can spray blood there. */
  onBleed: ((at: THREE.Vector3, dir: THREE.Vector3) => void) | null = null;
  /** The ground's height under a scene point: flat on the map, rolling hills out beyond it. */
  groundAt: (x: number, z: number) => number = () => 0;

  constructor() {
    const material = new THREE.MeshStandardMaterial({ roughness: 0.85 });
    const pool = (geo: THREE.BufferGeometry, n: number) => {
      const m = new THREE.InstancedMesh(geo, material, n);
      m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      // made now, so the shader is built with per-body colours from the first frame
      m.setColorAt(0, tmpC.set(0xffffff));
      m.count = 0;
      m.castShadow = true;
      // the bodies lie all over the field; their bounds would need working out again every frame
      m.frustumCulled = false;
      this.group.add(m);
      return m;
    };
    this.limbs = pool(capsule, MAX_BODIES * LIMBS);
    this.heads = pool(head, MAX_BODIES);
  }

  /**
   * A man killed at `at`, facing `heading` (radians, the scene's yaw): his body takes over from
   * his model. `push` is the velocity the hit gave him (tiles a second); `tear` the chance each
   * limb is torn off (a bullet tears nothing, a nuke nearly everything).
   */
  spawn(at: THREE.Vector3, heading: number, look: RagdollLook, push: THREE.Vector3, tear: number, now: number): void {
    const q = new THREE.Quaternion().setFromAxisAngle(UP, heading);
    const pos = new Float32Array(N * 3), prev = new Float32Array(N * 3);
    for (let i = 0; i < N; i++) {
      tmpA.set(...POSE[i]!).applyQuaternion(q).add(at);
      pos.set([tmpA.x, tmpA.y, tmpA.z], i * 3);
      // each point starts with the push, upper body a little more, so he folds as he goes
      const k = 1 + (POSE[i]![1] - 0.25) * 1.2 + (Math.random() - 0.5) * 0.25;
      tmpB.copy(push).multiplyScalar(k * STEP);
      prev.set([tmpA.x - tmpB.x, tmpA.y - tmpB.y, tmpA.z - tmpB.z], i * 3);
    }
    const dist = (a: number, b: number) => Math.hypot(pos[a * 3]! - pos[b * 3]!, pos[a * 3 + 1]! - pos[b * 3 + 1]!, pos[a * 3 + 2]! - pos[b * 3 + 2]!);
    const torn = new Set<number>();
    for (const r of LIMB_ROOTS) if (Math.random() < tear) torn.add(r);
    // the points of the torn limbs (elbow and hand, or knee and foot): no stiffener may hold them to the body any more
    const loose = new Set<number>();
    for (const r of torn) { loose.add(BONES[r]![1]); loose.add(BONES[r + 1]![1]); }
    const sticks: Stick[] = [
      ...BONES.map(([a, b], i) => ({ a, b, len: dist(a, b), i })).filter(s => !torn.has(s.i)),
      ...BRACES.filter(([a, b]) => !loose.has(a) && !loose.has(b)).map(([a, b]) => ({ a, b, len: dist(a, b) })),
    ];
    const colors = { uniform: look.uniform, skin: look.skin ?? 0xc08a64, boot: look.boot ?? 0x1f1d1a, trousers: look.trousers ?? look.uniform };
    const bodyColors = [...BONES.map(([, , , part]) => colors[part]), colors.skin];
    this.bodies.push({ pos, prev, sticks, colors: bodyColors, torn, at: now, bleedUntil: torn.size ? now + 1100 : 0 });
    while (this.bodies.length > MAX_BODIES) this.bodies.shift();
  }

  /** A blast at `centre`: bodies within `radius` tiles are thrown again, harder the nearer they lie. */
  kick(centre: THREE.Vector3, radius: number, power: number): void {
    for (const b of this.bodies) {
      const dx = b.pos[6]! - centre.x, dz = b.pos[8]! - centre.z;
      const d = Math.hypot(dx, dz);
      if (d > radius) continue;
      const f = Math.min(0.18, power * (1 - d / radius) * STEP);
      const ox = d > 1e-3 ? dx / d : 1, oz = d > 1e-3 ? dz / d : 0;
      for (let i = 0; i < N; i++) {
        const j = (0.9 + Math.random() * 0.2) * f;
        b.prev[i * 3]! -= ox * j;
        b.prev[i * 3 + 1]! -= 0.85 * j;
        b.prev[i * 3 + 2]! -= oz * j;
      }
    }
  }

  /** Clears every body (a new raid). */
  clear(): void {
    this.bodies = [];
    this.limbs.count = this.heads.count = 0;
  }

  update(now: number): void {
    const dt = Math.min(0.1, Math.max(0, (now - (this.last || now)) / 1000));
    this.last = now;
    this.acc += dt;
    while (this.acc >= STEP) { this.step(); this.acc -= STEP; }
    this.bodies = this.bodies.filter(b => now - b.at <= LIE_MS + SINK_MS);
    this.bodies.forEach((b, k) => {
      const age = now - b.at;
      const sink = age > LIE_MS ? ((age - LIE_MS) / SINK_MS) * 0.15 : 0;
      this.draw(b, k, sink);
      if (now < b.bleedUntil && this.onBleed && Math.random() < 0.14) {
        // a torn joint bleeds briefly where the limb was
        for (const r of b.torn) {
          const [a] = BONES[r]!;
          this.onBleed(tmpA.set(b.pos[a * 3]!, b.pos[a * 3 + 1]!, b.pos[a * 3 + 2]!).clone(), tmpD.set(Math.random() - 0.5, 0.5, Math.random() - 0.5).normalize().clone());
        }
      }
    });
    this.limbs.count = this.bodies.length * LIMBS;
    this.heads.count = this.bodies.length;
    for (const m of [this.limbs, this.heads]) {
      m.instanceMatrix.needsUpdate = true;
      m.instanceColor!.needsUpdate = true;
    }
  }

  private step(): void {
    const g = GRAVITY * STEP * STEP;
    for (const b of this.bodies) {
      const p = b.pos, q = b.prev;
      for (let i = 0; i < N; i++) {
        const x = i * 3;
        const vx = p[x]! - q[x]!, vy = p[x + 1]! - q[x + 1]!, vz = p[x + 2]! - q[x + 2]!;
        q[x] = p[x]!; q[x + 1] = p[x + 1]!; q[x + 2] = p[x + 2]!;
        p[x] = p[x]! + vx * 0.995; p[x + 1] = p[x + 1]! + vy * 0.995 - g; p[x + 2] = p[x + 2]! + vz * 0.995;
      }
      for (let it = 0; it < ITERATIONS; it++) {
        for (const s of b.sticks) {
          const a = s.a * 3, c = s.b * 3;
          const dx = p[c]! - p[a]!, dy = p[c + 1]! - p[a + 1]!, dz = p[c + 2]! - p[a + 2]!;
          const d = Math.hypot(dx, dy, dz) || 1e-6;
          const f = ((d - s.len) / d) * 0.5;
          p[a] = p[a]! + dx * f; p[a + 1] = p[a + 1]! + dy * f; p[a + 2] = p[a + 2]! + dz * f;
          p[c] = p[c]! - dx * f; p[c + 1] = p[c + 1]! - dy * f; p[c + 2] = p[c + 2]! - dz * f;
        }
        // the ground: nothing goes through it, and what touches it drags to a stop
        const base = this.groundAt(p[0]!, p[2]!);
        for (let i = 0; i < N; i++) {
          const y = i * 3 + 1;
          const floor = base + (i === P.head ? 0.045 : 0.02);
          if (p[y]! < floor) {
            p[y] = floor;
            const x = i * 3;
            q[x] = p[x]! - (p[x]! - q[x]!) * GROUND_KEEP ** STEP;
            q[x + 2] = p[x + 2]! - (p[x + 2]! - q[x + 2]!) * GROUND_KEEP ** STEP;
            if (q[y]! < p[y]!) q[y] = p[y]!;
          }
        }
      }
    }
  }

  /** Puts the k-th body's capsules between their points, and its head on its point. */
  private draw(b: Body, k: number, sink: number): void {
    BONES.forEach(([a, c, r], i) => {
      if (b.torn.has(i)) {
        // a severed joint does not stretch a capsule between the torso and the detached limb
        tmpS.setScalar(0);
        this.limbs.setMatrixAt(k * LIMBS + i, tmpM.compose(tmpA.set(0, -10, 0), NO_TURN, tmpS));
        this.limbs.setColorAt(k * LIMBS + i, tmpC.set(b.colors[i]!));
        return;
      }
      tmpA.set(b.pos[a * 3]!, b.pos[a * 3 + 1]! - sink, b.pos[a * 3 + 2]!);
      tmpB.set(b.pos[c * 3]!, b.pos[c * 3 + 1]! - sink, b.pos[c * 3 + 2]!);
      tmpD.subVectors(tmpB, tmpA);
      const len = Math.max(1e-3, tmpD.length());
      const drawnLen = Math.min(len, REST_LEN[i]! * 1.1);
      tmpQ.setFromUnitVectors(UP, tmpD.divideScalar(len));
      // the capsule is four units long and one thick: stretched to this bone's length, r thick
      tmpS.set(r, Math.max(0.001, (drawnLen + r) / 4), r);
      tmpA.add(tmpB).multiplyScalar(0.5);
      this.limbs.setMatrixAt(k * LIMBS + i, tmpM.compose(tmpA, tmpQ, tmpS));
      this.limbs.setColorAt(k * LIMBS + i, tmpC.set(b.colors[i]!));
    });
    tmpA.set(b.pos[0]!, b.pos[1]! - sink, b.pos[2]!);
    this.heads.setMatrixAt(k, tmpM.compose(tmpA, NO_TURN, tmpS.setScalar(0.045)));
    this.heads.setColorAt(k, tmpC.set(b.colors[LIMBS]!));
  }
}
